import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CidaasService } from 'src/app/service/cidaas.service';
import { CommonFunctionsService } from 'src/app/service/common-functions.service';
import { SharedDataService } from 'src/app/service/shared-data.service';
import { ILocation, LocationHelper } from '../../../helpers/location.helper';
import {KEY_DEFAULT_ERROR} from '../../../model/error.model';
import { PasswordFieldDefinition } from 'src/app/dynamic-form/components/util/password/password.model';
import { Field } from 'src/app/dynamic-form/components/util/shared/shared.model';

@Component({
    selector: 'app-password-set',
    templateUrl: './password-set.component.html',
    styleUrls: ['./password-set.component.scss'],
})
export class PasswordSetComponent implements OnInit, OnDestroy {
    fields: any[] = [];
    dynamicFields = ['password', 'password_echo'];
    formId;
    resetRequestId = '';
    requestId = '';
    exchangeId = '';
    loginSettingsId = '';
    errorMessage = '';
    regFieldError?: string;
    passwordPolicy: any;
    isSubmitBtnClicked = false;
    dynamicFormOptions = {
        btnEnabled: false,
    };
    buttonOptions: any = {
        resetPwd: {
            color: 'accent',
            text: 'PASSWORD_SET.RESET_BUTTON_TEXT',
            alignment: 'right',
            type: 'proceed',
            underline: 'none',
            for: 'cidaasSubmitBtn',
            formId: 'formId'
        }
    };
    location: ILocation = {
        lat: '',
        lon: '',
    };
    destroy$ = new Subject();
    contentLoading = false;

    routeParams$ = this.route.queryParams;
    translationStatus$ = this.dataService._getTranslationLoadStatus();

    constructor(
        private translate: TranslateService,
        private cidaasService: CidaasService,
        private activatedRoute: ActivatedRoute,
        private dataService: SharedDataService,
        public commonFunc: CommonFunctionsService,
        private route: ActivatedRoute,
    ) { }

    ngOnInit(): void {
        this.getLatLon();
        this._getPasswordPolicy();
        this.initQueryParamValues();

        this.contentLoading = true;
        this.dataService
            ._getTranslationLoadStatus()
            .pipe(takeUntil(this.destroy$))
            .subscribe((loaded) => {
                if (loaded) {
                    let options = {
                        acceptlanguage: 'de',
                        requestId: this.requestId,
                    };
                    this._getRegistrationFields(options);

                    if (this.fields && this.fields.length > 0) {
                        // added translation for old password field
                        this.fields.map((field: any) => {

                            // adding required translation string for the fields which dosen't have text in the fields localeText
                            if (!field?.localeText?.required) {
                                field.localeText.required = this.translate.instant('REQUIRED_FIELD_DYNAMIC', {
                                    field: field.localeText.name,
                                });
                            }
                        });

                        this.fields = [...this.addValidationForPassword(this.fields)];
                    }
                    this.contentLoading = false;
                }
            }, () => {
                this.contentLoading = false;
            });
    }
    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.unsubscribe();
    }

    getLatLon(): void {
        const lat = localStorage.getItem('lat');
        const lon = localStorage.getItem('lon');
        if (lat && lon) {
            this.location.lat = lat;
            this.location.lon = lon;
        } else {
            LocationHelper.getLocationInfo()
                .then((info: ILocation) => {
                    this.location = info;
                })
                .catch((error) => {
                    this.location.lat = '';
                    this.location.lon = '';
                });
        }
    }
    _getFormId = (e) => {
        this.formId = e;
    };
    _getPasswordPolicy = () => {
        this.dataService._getClientInfo().subscribe((data) => {
            if (data.password_policy) {
                this.passwordPolicy = data.password_policy;
            }
        });
    };

    // initialize query params
    initQueryParamValues() {
        this.activatedRoute.queryParams.subscribe((params) => {
            this.requestId = params.requestId || params.requestid;
            this.resetRequestId = params.rprq;
            this.exchangeId = params.exchangeid || params.exchangeId;
            this.loginSettingsId = params.lsid;
            // if (params.error) {
            //   this.translate.get('PASSWORD_SET.OLD_PWD_INCORRECT').subscribe((translationForOldPasswordError) => {
            //     this.errorMessage = translationForOldPasswordError;
            //   });
            // }
        });
    }

    _getRegistrationFields = (options: any) => {
        this.cidaasService
            ._getRegistrationSetup(options)
            .then((response: any) => {
                if (response && response.data) {
                    let data = response.data.sort(function (a, b) {
                        var keyA = a.order,
                            keyB = b.order;
                        if (keyA < keyB) return -1;
                        if (keyA > keyB) return 1;
                        return 0;
                    });
                    this.fields = data.filter((registerField) => registerField.dataType === 'PASSWORD' && registerField.fieldType === "SYSTEM");
                    if (this.loginSettingsId) {
                        // will add translated field name and required errormessage to the old password field object
                        const fieldName = this.translate.instant('PASSWORD_SET.PASSWORD_OLD');
                        const requiredMessage = this.translate.instant('PASSWORD_SET.PASSWORD_OLD_REQUIRED_MESSAGE');

                        this.fields.unshift({
                            dataType: 'PASSWORD',
                            parent_group_id: 'DEFAULT',
                            is_group: false,
                            fieldKey: 'password_old',
                            fieldType: 'SYSTEM',
                            order: 20,
                            readOnly: false,
                            required: true,
                            localeText: { locale: 'en-us', name: fieldName, required: requiredMessage},
                            language: 'en',
                            skipPasswordPolicy: true
                        });
                    }
                    this.fields = this.addValidationForPassword(this.fields);
                } else {
                    this.errorMessage = KEY_DEFAULT_ERROR;
                }
            })
            .catch((error) => {
                console.error(error);
            });
    };

    addValidationForPassword = (fields: any[]): any[] => {
        if (!fields?.length) {
            this.regFieldError = 'PASSWORD_SET.ERROR_NO_REGISTRATION_FIELD';
            return [];
        }

        /**
         * adding required as true for the password fields
         * why we are adding? because there are chances of setting the password fields are optional
         * in the registration field setup
         */
        fields.map((field)=>{ field.required = true})

        const passwordEchoFieldIndex = fields.findIndex((field) => field.fieldDefinition?.matchWith);
        const passwordFieldIndex = fields.findIndex((field) => !field.skipPasswordPolicy && !field?.fieldDefinition?.matchWith);
        const foundPassField = fields[passwordFieldIndex];
        if (!foundPassField) {
            this.regFieldError = 'PASSWORD_SET.ERROR_PASSWORD_FIELD_NOT_FOUND';
            return fields;
        }
        this.regFieldError = undefined;
        if (this.passwordPolicy) {
            fields[passwordFieldIndex] = this._addPasswordPolicy(foundPassField);
        }
        if (passwordEchoFieldIndex !== -1) {
            fields[passwordEchoFieldIndex] = this.removeFieldConstraints(fields[passwordEchoFieldIndex]);
        }
        return fields;
    };

    _addPasswordPolicy = (field: any) => {
        let validations = ['lowerAndUpperCase', 'maximumLength', 'minimumLength', 'noOfDigits', 'noOfSpecialChars'];
        validations.forEach((key) => {
            if (validations.includes(key)) {
                if (key == 'maximumLength') {
                    field.fieldDefinition['maxLength'] = this.passwordPolicy['maximumLength'] || undefined;
                    let translationValue = (field.fieldDefinition['maxLength'] === 1)? "FORM.VALIDATION.PASSWORD.POLICY.CHAR_MAX_ONE" : "FORM.VALIDATION.PASSWORD.POLICY.CHAR_MAX";
                    this.translate
                        .get(translationValue, { count: field.fieldDefinition['maxLength'] })
                        .subscribe((res) => {
                            field.localeText['maxLength'] = res;
                        });
                }
                if (key == 'minimumLength') {
                    field.fieldDefinition['minLength'] = this.passwordPolicy['minimumLength'] || undefined;
                    let translationValue = (field.fieldDefinition['minLength'] === 1)? "FORM.VALIDATION.PASSWORD.POLICY.CHAR_MIN_ONE" : "FORM.VALIDATION.PASSWORD.POLICY.CHAR_MIN";
                    this.translate
                        .get(translationValue, { count: field.fieldDefinition['minLength'] })
                        .subscribe((res) => {
                            field.localeText['minLength'] = res;
                        });
                }
                if (key == 'noOfDigits') {
                    field.fieldDefinition['digitsLength'] = this.passwordPolicy['noOfDigits'] || undefined;
                    let translationValue = (field.fieldDefinition['digitsLength'] === 1)? "FORM.VALIDATION.PASSWORD.POLICY.CHAR_DIGIT_ONE" : "FORM.VALIDATION.PASSWORD.POLICY.CHAR_DIGIT";
                    this.translate
                        .get(translationValue, { count: field.fieldDefinition['digitsLength'] })
                        .subscribe((res) => {
                            field.localeText['digitsLength'] = res;
                        });
                }
                if (key == 'noOfSpecialChars') {
                    field.fieldDefinition['specialLength'] =
                        this.passwordPolicy['noOfSpecialChars'] || undefined;
                    let translationValue = (field.fieldDefinition['specialLength'] === 1)? "FORM.VALIDATION.PASSWORD.POLICY.CHAR_SPECIAL_ONE" : "FORM.VALIDATION.PASSWORD.POLICY.CHAR_SPECIAL";
                    this.translate
                        .get(translationValue, { count: field.fieldDefinition['specialLength'] })
                        .subscribe((res) => {
                            field.localeText['specialLength'] = res;
                        });
                }
                if (key == 'lowerAndUpperCase') {
                    field.fieldDefinition['lowerupperCase'] = this.passwordPolicy['lowerAndUpperCase'] || undefined;
                    this.translate.get('FORM.VALIDATION.PASSWORD.POLICY.CHAR_LOWER_UPPER').subscribe((res) => {
                        field.localeText['lowerupperCase'] = res;
                    });
                }
            }
        });
        return field;
    };
    _triggerFormSubmit = () => {
        this.isSubmitBtnClicked = true;
        setTimeout(() => {
            this.isSubmitBtnClicked = false;
        });
    };

    // call reset password with password and confir, password
    sendResetPassword(e) {
        let resetPayload: any = {};
        if (this.loginSettingsId) {
            this.callCidaasFirstTimeChangePassword(resetPayload, e);
        } else {
            this.callCidaasResetPassword(resetPayload, e);
        }
    }

    // call first time reset password with new, old and confirm pwd
    private callCidaasFirstTimeChangePassword(resetPayload: any, e: any) {
        resetPayload = {
            loginSettingsId: this.loginSettingsId,
            new_password: e.password,
            old_password: e.password_old,
            confirm_password: e.password_echo,
            lat: this.location.lat,
            lon: this.location.lon
        };
        this.contentLoading = true;
        this.cidaasService.firstTimeChangePassword(resetPayload);
    }

    private callCidaasResetPassword(resetPayload: any, e: any) {
        resetPayload = {
            resetRequestId: this.resetRequestId,
            exchangeId: this.exchangeId,
            password: e.password,
            confirmPassword: e.password_echo,
        };
        this.contentLoading = true;
        this.cidaasService.resetPassword(resetPayload);
    }

    removeFieldConstraints(field: Field<PasswordFieldDefinition>): Field<PasswordFieldDefinition> {
        if (field?.fieldDefinition) {
            field.fieldDefinition.maxLength = undefined;
            field.fieldDefinition.minLength = undefined;
        }
        return field;
    }
}
